import { CaptureContext } from "@zumer/snapdom";

/**
 * Snapdom plugin that keeps legend item titles from being chopped in captures.
 *
 * Problem:
 *   A `.legend-item-title` fills the width its flex wrapper gives it on screen
 *   (the wrapper is `width: 100%`). snapdom does not carry that wrapper width
 *   onto the clone, so in the capture the wrapper collapses to its content and
 *   the title shrink-wraps to a hair under its on-screen width. The trailing
 *   word/character then wraps onto a line the legend box clips — dropping text
 *   that is fully visible on screen. Wide legends lose the last word of every
 *   item; narrow (wrapping) legends lose the last character of the last line.
 *
 * Solution:
 *   After the clone is created, pin each cloned title to the exact width its
 *   wrapper has on screen (and min-width:0 so it can still wrap inside that
 *   width). The title then lays out in the capture exactly as it does live —
 *   no heuristics, just the measured width. Only affects screenshot captures;
 *   the live legend is untouched.
 *
 * Note: the screenshot legend renders the title alone (no count badge), so the
 * wrapper's full content width is the title's available width.
 */

const TITLE_SELECTOR = ".legend-item-title";

const fitClonedLegendTitles = (liveRoot: Element, clone: HTMLElement): void => {
  const liveTitles = liveRoot.querySelectorAll<HTMLElement>(TITLE_SELECTOR);
  const cloneTitles = clone.querySelectorAll<HTMLElement>(TITLE_SELECTOR);

  cloneTitles.forEach((cloneTitle, index) => {
    const liveTitle = liveTitles[index];
    const wrapper = liveTitle?.parentElement;
    if (!wrapper) return;

    const availableWidth = wrapper.clientWidth;
    if (!availableWidth) return;

    cloneTitle.style.width = `${availableWidth}px`;
    cloneTitle.style.minWidth = "0";
  });
};

const legendTitleWidthPlugin = {
  name: "legend-title-width",
  async afterClone(context: CaptureContext) {
    if (context.element && context.clone instanceof HTMLElement) {
      fitClonedLegendTitles(context.element, context.clone);
    }
  }
};

export default legendTitleWidthPlugin;
